import {
  lazy,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import Skeleton from "@/components/ui/Skeleton";
import { useLoadingIndicator } from "@/hooks/useLoadingIndicator";

/**
 * The route-level loading surface and the `lazyPage` helper
 * (ENG-APP-UX-TUNEUP Track F, floor row 10).
 *
 * Every page under `pages/` is code-split: `lazyPage(() => import(...))`
 * returns a component that suspends on its own chunk INSIDE the shell, so
 * the header and sidebar stay put while a route's chunk loads. The fallback
 * follows the loading policy (`useLoadingIndicator`: nothing under 200 ms,
 * then a skeleton that stays ≥ 300 ms) — a fast chunk never flashes.
 */
export function RouteFallback() {
  const { show } = useLoadingIndicator(true);
  if (!show) return null;
  return (
    <div className="px-6 py-8" aria-busy="true">
      <Skeleton lines={5} className="max-w-2xl" />
    </div>
  );
}

type PageProps = Record<string, unknown>;

const RELOAD_GUARD_KEY = "aiaso:chunk-reload";

/**
 * A chunk that is not there any more. After a deploy the hashed chunk names
 * change; a user still on the previous entry who navigates to a route they
 * have not visited asks the CDN for a chunk that no longer exists and the
 * dynamic import rejects. The honest recovery is ONE full reload (the new
 * entry has the new names), guarded by sessionStorage so a genuinely broken
 * deploy never loops -- the second failure surfaces in the ErrorBoundary.
 */
function recoverStaleChunk<T>(error: unknown): Promise<T> {
  const message = error instanceof Error ? error.message : String(error);
  const stale =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
      message,
    );
  if (stale && typeof window !== "undefined") {
    let reloadedAlready: boolean;
    try {
      reloadedAlready = window.sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
      if (!reloadedAlready)
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    } catch {
      // Storage unavailable (blocked site data): fail CLOSED. Reloading
      // without a guard looped ~6 reloads/s on a persistently failing
      // chunk (new-user funnel proof 2026-10-17); the ErrorBoundary is
      // the right landing instead.
      reloadedAlready = true;
    }
    if (!reloadedAlready) {
      window.location.reload();
      // Never resolves: the reload replaces the document.
      return new Promise<T>(() => {});
    }
  }
  throw error;
}

/** Clears the one-reload guard once a chunk loads, so the NEXT deploy may reload too. */
function chunkLoaded<T>(value: T): T {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // storage unavailable: nothing to clear
  }
  return value;
}

/** A code-split page with its own Suspense boundary (see above). */
export function lazyPage<P extends PageProps = PageProps>(
  loader: () => Promise<{ default: ComponentType<P> }>,
): ComponentType<P> {
  type Loaded = { default: ComponentType<P> };
  const Page: LazyExoticComponent<ComponentType<P>> = lazy(() =>
    loader().then(chunkLoaded<Loaded>, recoverStaleChunk<Loaded>),
  );
  function LazyPage(props: P) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Page {...props} />
      </Suspense>
    );
  }
  return LazyPage;
}

export default RouteFallback;
